/**
 * Pure computation functions for the meal plan page.
 * No React imports, no side effects — testable with plain data.
 */

import type { Consumption, Recipe, Week } from "@/client/api";
import type { MissingRecipe, StockMap, UnplannedConsumptionRow } from "@/client/pages/plan/types";
import { dateStrForDayIndex, totalConsumedPerRecipe } from "@/client/pages/plan/types";
import { APP_LOCALE } from "@/shared/locale";

/** IDs of recipes that need stock tracking (have meal-prep steps or have step components). */
export function buildNeedsStockSet(recipes: Recipe[]): Set<string> {
  const set = new Set<string>();
  for (const r of recipes) {
    if (r.has_meal_prep_steps || (r.component_recipes?.length ?? 0) > 0) set.add(r.id);
  }
  return set;
}

/** Finds recipes planned for the week that don't have enough stock. */
export function getMissingRecipes(
  week: Week,
  stockMap: StockMap,
  consumptionMap: Map<string, number>,
  needsStock: Set<string>,
): MissingRecipe[] {
  const consumed = totalConsumedPerRecipe(consumptionMap);
  const needed = new Map<string, { recipe_name: string; recipe_type: string; total: number }>();
  for (const slot of week.slots) {
    for (const r of slot.recipes) {
      if (!needsStock.has(r.recipe_id)) continue;
      const prev = needed.get(r.recipe_id);
      if (prev) {
        prev.total += r.servings;
      } else {
        needed.set(r.recipe_id, {
          recipe_name: r.recipe_name,
          recipe_type: r.recipe_type,
          total: r.servings,
        });
      }
    }
  }
  const missing: MissingRecipe[] = [];
  for (const [id, info] of needed) {
    const stock = stockMap.get(id)?.servings ?? 0;
    const alreadyConsumed = consumed.get(id) ?? 0;
    const remaining = info.total - alreadyConsumed;
    if (remaining > 0 && stock < remaining) {
      missing.push({
        recipe_id: id,
        recipe_name: info.recipe_name,
        recipe_type: info.recipe_type,
        deficit: remaining - stock,
      });
    }
  }
  return missing.sort((a, b) => a.recipe_name.localeCompare(b.recipe_name, APP_LOCALE));
}

/** Groups consumptions for a day that don't match any planned recipe slot. */
export function unplannedConsumptionsForDay(
  consumptions: Consumption[],
  dateStr: string,
  plannedRecipeIds: Set<string>,
): UnplannedConsumptionRow[] {
  const byRecipe = new Map<string, { recipe_name: string; servings: number; ids: string[] }>();
  for (const c of consumptions) {
    if (c.consumption_date !== dateStr) continue;
    if (plannedRecipeIds.has(c.recipe_id)) continue;
    const prev = byRecipe.get(c.recipe_id);
    if (prev) {
      prev.servings += c.servings_consumed;
      prev.ids.push(c.id);
    } else {
      byRecipe.set(c.recipe_id, {
        recipe_name: c.recipe_name,
        servings: c.servings_consumed,
        ids: [c.id],
      });
    }
  }
  return [...byRecipe.entries()]
    .map(([recipe_id, v]) => ({
      recipe_id,
      recipe_name: v.recipe_name,
      servings: v.servings,
      consumption_ids: v.ids,
    }))
    .sort((a, b) => a.recipe_name.localeCompare(b.recipe_name, APP_LOCALE));
}

/**
 * Augments the stock map with availability for non-storable recipes that have components,
 * and builds a consumption map from raw consumption data.
 * Also infers consumption for non-storable recipes via greedy allocation from component consumption.
 */
export function buildMapsWithInference(
  stock: StockMap,
  recipes: Recipe[],
  consumptions: Consumption[],
  week: Week | null,
): { stockMap: StockMap; consumptionMap: Map<string, number> } {
  const stockMap = new Map(stock);

  // Add availability for non-storable recipes with components
  for (const r of recipes) {
    if (!r.has_meal_prep_steps && (r.component_recipes?.length ?? 0) > 0 && !stockMap.has(r.id)) {
      stockMap.set(r.id, { servings: r.available_servings ?? 0, oldestBatchId: null });
    }
  }

  // Build base consumption map
  const consumptionMap = new Map<string, number>();
  for (const c of consumptions) {
    const key = `${c.recipe_id}:${c.consumption_date}`;
    consumptionMap.set(key, (consumptionMap.get(key) ?? 0) + c.servings_consumed);
  }

  // Infer consumption for non-storable recipes with components (greedy allocation)
  if (week) {
    const plannedWithComponents: Recipe[] = [];
    const seen = new Set<string>();
    for (const slot of week.slots) {
      for (const r of slot.recipes) {
        if (seen.has(r.recipe_id)) continue;
        seen.add(r.recipe_id);
        const rec = recipes.find((rx) => rx.id === r.recipe_id);
        if (rec && !rec.has_meal_prep_steps && (rec.component_recipes?.length ?? 0) > 0)
          plannedWithComponents.push(rec);
      }
    }
    for (let i = 0; i < 7; i++) {
      const dateStr = dateStrForDayIndex(week.start_date, i);
      const pool = new Map<string, number>();
      for (const recipe of plannedWithComponents) {
        for (const c of recipe.component_recipes!) {
          if (!pool.has(c.recipe_id)) {
            pool.set(c.recipe_id, consumptionMap.get(`${c.recipe_id}:${dateStr}`) ?? 0);
          }
        }
      }
      for (const recipe of plannedWithComponents) {
        const timesMade = Math.min(
          ...recipe.component_recipes!.map((c) =>
            Math.floor((pool.get(c.recipe_id) ?? 0) / c.total_servings_needed),
          ),
        );
        if (timesMade > 0) {
          consumptionMap.set(`${recipe.id}:${dateStr}`, timesMade * (recipe.servings ?? 1));
          for (const c of recipe.component_recipes!) {
            pool.set(
              c.recipe_id,
              (pool.get(c.recipe_id) ?? 0) - timesMade * c.total_servings_needed,
            );
          }
        }
      }
    }
  }

  return { stockMap, consumptionMap };
}

/**
 * Resolves missing recipes → base components that need prepping.
 * Returns a sorted list of recipes to cook with servings_to_cook.
 */
export function computeRecipesToPrep(
  missingRecipes: MissingRecipe[],
  recipes: Recipe[],
  stockMap: StockMap,
): { recipe_id: string; recipe_name: string; recipe_type: string; servings_to_cook: number }[] {
  const componentDeficit = new Map<string, number>();
  const result: {
    recipe_id: string;
    recipe_name: string;
    recipe_type: string;
    servings_to_cook: number;
  }[] = [];

  for (const r of missingRecipes) {
    const rec = recipes.find((rx) => rx.id === r.recipe_id);
    const components = rec?.component_recipes ?? [];

    const recipeServings = rec?.servings ?? 1;
    for (const comp of components) {
      const needed = comp.total_servings_needed * (r.deficit / recipeServings);
      componentDeficit.set(comp.recipe_id, (componentDeficit.get(comp.recipe_id) ?? 0) + needed);
    }

    if (rec?.has_meal_prep_steps) {
      const recipeYield = rec.servings ?? 1;
      result.push({
        recipe_id: r.recipe_id,
        recipe_name: r.recipe_name,
        recipe_type: r.recipe_type,
        servings_to_cook: Math.max(recipeYield, r.deficit),
      });
    }
  }

  const seen = new Set(result.map((r) => r.recipe_id));
  for (const [childId, totalNeeded] of componentDeficit) {
    if (seen.has(childId)) continue;
    seen.add(childId);
    const child = recipes.find((rec) => rec.id === childId);
    if (!child) continue;
    const currentStock = stockMap.get(childId)?.servings ?? 0;
    const deficit = totalNeeded - currentStock;
    if (deficit <= 0) continue;
    const recipeYield = child.servings ?? 1;
    result.push({
      recipe_id: child.id,
      recipe_name: child.name,
      recipe_type: child.type ?? "other",
      servings_to_cook: Math.max(recipeYield, deficit),
    });
  }

  return result.sort((a, b) => a.recipe_name.localeCompare(b.recipe_name, APP_LOCALE));
}
